import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { validateQuoteData, validateComparisonMetrics } from '@/lib/validations/quote-schemas';
import type { 
  EnhancedPEOQuote, 
  QuoteLineItem, 
  QuotePlanOption, 
  QuoteImplementationMilestone,
  QuoteComparisonBaseline,
  QuoteSavingsAnalysis,
  ComparisonMetrics
} from '@/types/quotes-enhanced.types';

export const useEnhancedQuotes = (applicationId?: string) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Fetch enhanced quotes with all related data
  const {
    data: quotes = [],
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['enhanced-quotes', applicationId],
    queryFn: async (): Promise<EnhancedPEOQuote[]> => {
      if (!user || !applicationId) {
        throw new Error('User or application ID not found');
      }

      console.log('Fetching enhanced quotes for application:', applicationId);

      // Fetch quotes with enhanced data
      const { data: quotesData, error: quotesError } = await supabase
        .from('peo_quotes')
        .select(`
          id,
          application_id,
          provider_id,
          quote_summary,
          quote_file_url,
          admin_notes,
          status,
          monthly_cost,
          annual_cost,
          created_at,
          updated_at,
          created_by,
          valid_until,
          is_submitted,
          peo_providers(name)
        `)
        .eq('application_id', applicationId)
        .order('created_at', { ascending: false });

      if (quotesError) throw quotesError;

      // Fetch line items for all quotes
      const quoteIds = quotesData.map(q => q.id);
      const [lineItemsResult, planOptionsResult, timelineResult] = await Promise.all([
        supabase
          .from('quote_line_items')
          .select(`
            *,
            category:quote_cost_categories(*)
          `)
          .in('quote_id', quoteIds),
        supabase
          .from('quote_plan_options')
          .select('*')
          .in('quote_id', quoteIds),
        supabase
          .from('quote_implementation_timeline')
          .select('*')
          .in('quote_id', quoteIds)
          .order('sort_order')
      ]);

      if (lineItemsResult.error) throw lineItemsResult.error;
      if (planOptionsResult.error) throw planOptionsResult.error;
      if (timelineResult.error) throw timelineResult.error;

      // Combine the data with validation
      const enhancedQuotes: EnhancedPEOQuote[] = quotesData.map(quote => {
        const combinedQuote = {
          ...quote,
          provider_name: (quote as any).peo_providers?.name,
          line_items: lineItemsResult.data?.filter(item => item.quote_id === quote.id) || [],
          plan_options: planOptionsResult.data?.filter(option => option.quote_id === quote.id) || [],
          implementation_timeline: timelineResult.data?.filter(milestone => milestone.quote_id === quote.id) || []
        };

        try {
          return validateQuoteData(combinedQuote);
        } catch (error) {
          console.warn('Quote validation failed for quote:', quote.id, error);
          // Return the data anyway but log the validation error
          return combinedQuote as EnhancedPEOQuote;
        }
      });

      console.log('Fetched enhanced quotes:', enhancedQuotes.length);
      return enhancedQuotes;
    },
    enabled: !!user && !!applicationId
  });

  // Fetch comparison baseline
  const { data: baseline } = useQuery({
    queryKey: ['comparison-baseline', applicationId],
    queryFn: async (): Promise<QuoteComparisonBaseline | null> => {
      if (!applicationId) return null;

      const { data, error } = await supabase
        .from('quote_comparison_baselines')
        .select('*')
        .eq('application_id', applicationId)
        .single();

      if (error && error.code !== 'PGRST116') throw error;
      return data;
    },
    enabled: !!applicationId
  });

  // Calculate savings for a quote
  const calculateSavings = async (quoteId: string): Promise<QuoteSavingsAnalysis | null> => {
    try {
      const { data, error } = await supabase
        .rpc('calculate_quote_savings', { quote_id_param: quoteId });

      if (error) throw error;
      return data[0] || null;
    } catch (error) {
      console.error('Error calculating savings:', error);
      return null;
    }
  };

  // Get comparison metrics for all quotes - standardized to always return Promise
  const getComparisonMetrics = async (): Promise<ComparisonMetrics[]> => {
    try {
      if (!quotes.length) return [];

      console.log('Calculating comparison metrics for', quotes.length, 'quotes');
      const metrics: ComparisonMetrics[] = [];

      for (const quote of quotes) {
        try {
          const savings = await calculateSavings(quote.id);
          const implementationDays = quote.implementation_timeline.reduce(
            (total, milestone) => total + (milestone.estimated_duration_days || 0), 
            0
          );
          
          // Calculate service score based on included services
          const includedServices = quote.line_items.filter(item => item.is_included).length;
          const totalServices = quote.line_items.length;
          const serviceScore = totalServices > 0 ? (includedServices / totalServices) * 100 : 0;

          // Calculate recommendation score (based on savings, service score, implementation time)
          const savingsScore = Math.min((savings?.savings_percentage || 0) * 2, 100);
          const timeScore = Math.max(100 - (implementationDays / 90) * 100, 0);
          const recommendationScore = (savingsScore * 0.4 + serviceScore * 0.3 + timeScore * 0.3);

          metrics.push({
            quote_id: quote.id,
            provider_name: quote.provider_name || 'Unknown Provider',
            total_cost: quote.annual_cost || savings?.total_proposed_cost || 0,
            savings_amount: savings?.total_savings || 0,
            savings_percentage: savings?.savings_percentage || 0,
            implementation_days: implementationDays,
            service_score: Math.round(serviceScore),
            recommendation_score: Math.round(recommendationScore)
          });
        } catch (error) {
          console.error(`Error calculating metrics for quote ${quote.id}:`, error);
          // Add fallback metric with basic data
          metrics.push({
            quote_id: quote.id,
            provider_name: quote.provider_name || 'Unknown Provider',
            total_cost: quote.annual_cost || 0,
            savings_amount: 0,
            savings_percentage: 0,
            implementation_days: 0,
            service_score: 0,
            recommendation_score: 0
          });
        }
      }

      console.log('Calculated metrics for', metrics.length, 'quotes');
      const sortedMetrics = metrics.sort((a, b) => b.recommendation_score - a.recommendation_score);
      
      try {
        return validateComparisonMetrics(sortedMetrics);
      } catch (error) {
        console.warn('Metrics validation failed:', error);
        return sortedMetrics;
      }
    } catch (error) {
      console.error('Error in getComparisonMetrics:', error);
      return [];
    }
  };

  // Save comparison baseline
  const saveBaselineMutation = useMutation({
    mutationFn: async (baseline: Partial<QuoteComparisonBaseline>) => {
      if (!applicationId) throw new Error('Application ID required');

      const { data, error } = await supabase
        .from('quote_comparison_baselines')
        .upsert({
          application_id: applicationId,
          ...baseline
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['comparison-baseline'] });
      toast.success('Comparison baseline saved successfully');
    },
    onError: (error) => {
      console.error('Error saving baseline:', error);
      toast.error('Failed to save comparison baseline');
    }
  });

  return {
    quotes,
    baseline,
    isLoading,
    error,
    refetch,
    calculateSavings,
    getComparisonMetrics,
    saveBaseline: saveBaselineMutation.mutate,
    isSavingBaseline: saveBaselineMutation.isPending
  };
};